import axios from "axios";
import Swal from "sweetalert2";
import { apiurl } from "utils";

const api = axios.create({
  baseURL: apiurl,
});

export const SESSION_STORAGE_KEYS = [
  "tk446655440000",
  "ps446655440000",
  "accessToken",
  "refreshToken",
  "token",
  "user",
  "userData",
  "permissions",
  "mapData",
];
export const ACCOUNT_DEACTIVATED_CODE = "ACCOUNT_DEACTIVATED";
export const ACCOUNT_DEACTIVATED_MESSAGE = "Your account has been deactivated. Please contact an administrator.";
const DEACTIVATED_NOTICE_KEY = "everifpro_account_deactivated_notice";

export function clearSession() {
  SESSION_STORAGE_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
}

let redirectingToLogin = false;
let permissionAlertVisible = false;
let deactivatedAlertVisible = false;
let authenticatedRequestController = new AbortController();

export function isAccountDeactivatedError(error) {
  const code = error?.response?.data?.code ?? error?.response?.data?.Code;
  return error?.response?.status === 401 && code === ACCOUNT_DEACTIVATED_CODE;
}

export function httpErrorAction(status, code) {
  if (status === 401 && code === ACCOUNT_DEACTIVATED_CODE) return "deactivated";
  if (status === 401) return "logout";
  if (status === 403) return "forbidden";
  return "none";
}

function abortAuthenticatedRequests() {
  authenticatedRequestController.abort();
  authenticatedRequestController = new AbortController();
}

function showDeactivatedMessageOnce() {
  if (deactivatedAlertVisible) return;
  deactivatedAlertVisible = true;
  Promise.resolve(Swal.fire({
    icon: "error",
    title: "Account deactivated",
    text: ACCOUNT_DEACTIVATED_MESSAGE,
    confirmButtonText: "OK",
  })).finally(() => {
    deactivatedAlertVisible = false;
  });
}

export function consumeAccountDeactivatedNotice() {
  if (sessionStorage.getItem(DEACTIVATED_NOTICE_KEY) !== "1") return false;
  sessionStorage.removeItem(DEACTIVATED_NOTICE_KEY);
  showDeactivatedMessageOnce();
  return true;
}

export function handleHttpError(error) {
  const status = error?.response?.status;
  const code = error?.response?.data?.code ?? error?.response?.data?.Code;
  const action = httpErrorAction(status, code);
  if (action === "deactivated") {
    clearSession();
    abortAuthenticatedRequests();
    if (window.location.pathname === "/sign-in") {
      showDeactivatedMessageOnce();
    } else if (!redirectingToLogin) {
      redirectingToLogin = true;
      sessionStorage.setItem(DEACTIVATED_NOTICE_KEY, "1");
      window.location.replace("/sign-in");
    }
  } else if (action === "logout") {
    clearSession();
    abortAuthenticatedRequests();
    if (!redirectingToLogin && window.location.pathname !== "/sign-in") {
      redirectingToLogin = true;
      window.location.replace("/sign-in");
    }
  } else if (action === "forbidden" && !permissionAlertVisible) {
    permissionAlertVisible = true;
    Swal.fire({
      icon: "error",
      title: "Permission refusée",
      text: "Vous n’avez pas l’autorisation d’effectuer cette action.",
      confirmButtonText: "OK",
    }).finally(() => {
      permissionAlertVisible = false;
    });
  }
  return Promise.reject(error);
}

function addApiKey(config) {
  const url = String(config?.url || "").toLowerCase();
  if (url.startsWith("/web/") || url.startsWith("web/")) return config;
  const apiKey = process.env.REACT_APP_API_KEY;
  if (apiKey) {
    config.headers["X-ApiKey"] = apiKey;
  }
  return config;
}

function trackAuthenticatedRequest(config) {
  const url = String(config?.url || "").toLowerCase();
  const isLogin = url.includes("auth/login");
  const hasSession = Boolean(localStorage.getItem("tk446655440000"));
  const hasAuthorization = Boolean(config?.headers?.Authorization || config?.headers?.authorization);
  if (!isLogin && (hasSession || hasAuthorization) && !config.signal) {
    config.signal = authenticatedRequestController.signal;
  }
  return config;
}

api.interceptors.request.use((config) => trackAuthenticatedRequest(addApiKey(config)));

// Add a response interceptor
api.interceptors.response.use((response) => response, handleHttpError);

// Some older screens use the default axios client directly. Apply the same
// session/permission behavior until those requests are migrated to `api`.
axios.interceptors.response.use((response) => response, handleHttpError);
axios.interceptors.request.use(trackAuthenticatedRequest);

// Test helper; production code never needs to call this.
export function resetHttpErrorStateForTests() {
  redirectingToLogin = false;
  permissionAlertVisible = false;
  deactivatedAlertVisible = false;
  sessionStorage.removeItem(DEACTIVATED_NOTICE_KEY);
  authenticatedRequestController = new AbortController();
}

export default api;
